import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  FOOD_ORDERS, MY_FOOD_ORDERS, CREATE_FOOD_ORDER, JOIN_FOOD_ORDER,
  LEAVE_FOOD_ORDER, UPDATE_FOOD_ORDER_STATUS, UPDATE_MY_FOOD_ITEMS,
} from '../graphql';
import {
  UtensilsCrossed, MapPin, Clock, Users, Plus, X, ExternalLink,
  Pencil, LogOut, StickyNote, Building2, Loader2, AlertCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUSES = [
  { value: 'OPEN',      label: 'Open',      color: 'bg-green-100 text-green-700' },
  { value: 'ORDERED',   label: 'Ordered',   color: 'bg-amber-100 text-amber-700' },
  { value: 'DELIVERED', label: 'Delivered', color: 'bg-blue-100 text-blue-700' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-gray-100 text-gray-500' },
];

// Host actions available from each status
const HOST_ACTIONS = {
  OPEN:      [{ to: 'ORDERED', label: 'Mark ordered' }, { to: 'CANCELLED', label: 'Cancel' }],
  ORDERED:   [{ to: 'DELIVERED', label: 'Mark delivered' }, { to: 'CANCELLED', label: 'Cancel' }],
  DELIVERED: [],
  CANCELLED: [{ to: 'OPEN', label: 'Reopen' }],
};

const toDate = (v) => {
  if (!v) return null;
  const n = Number(v);
  const d = new Date(Number.isNaN(n) ? v : n);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (v) => {
  const d = toDate(v);
  return d ? d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—';
};

const isPast = (v) => {
  const d = toDate(v);
  return d ? d.getTime() < Date.now() : false;
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function StatusBadge({ status }) {
  const cfg = STATUSES.find(s => s.value === status) || { label: status, color: 'bg-gray-100 text-gray-500' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>;
}

function OrderCard({ order, currentUserId, onJoin, onLeave, onStatus, onUpdateItems, busy }) {
  const isHost = String(order.creator?.id) === String(currentUserId);
  const myMembership = order.members?.find(m => String(m.user?.id) === String(currentUserId));
  const isFull = order.memberCount >= order.maxPeople;
  const cutoffPast = isPast(order.cutoffTime);
  const canJoin = !order.isMember && !isHost && order.status === 'OPEN' && !isFull;

  const [joining, setJoining] = useState(false);
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState('');
  const [amount, setAmount] = useState('');
  const [showMembers, setShowMembers] = useState(false);

  const startEdit = () => {
    setItems(myMembership?.items || '');
    setAmount(myMembership?.amount ? String(myMembership.amount) : '');
    setEditing(true);
  };

  const submitJoin = async () => {
    await onJoin(order.id, items.trim() || null, amount ? parseFloat(amount) : null);
    setJoining(false); setItems(''); setAmount('');
  };

  const submitEdit = async () => {
    await onUpdateItems(order.id, items.trim() || null, amount ? parseFloat(amount) : null);
    setEditing(false);
  };

  return (
    <div className="card !p-5 hover:shadow-md transition-shadow">
      {/* Top row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600 flex-shrink-0">
            <UtensilsCrossed size={18} />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 leading-tight">
              {order.restaurant}
              {order.restaurantUrl && (
                <a href={order.restaurantUrl} target="_blank" rel="noopener noreferrer" className="inline-flex align-middle ml-1.5 text-indigo-500 hover:text-indigo-700"><ExternalLink size={13} /></a>
              )}
            </h3>
            <p className="text-xs text-gray-400">Hosted by {isHost ? 'you' : order.creator?.name}</p>
          </div>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* Details */}
      <div className="space-y-1.5 text-sm text-gray-600 mb-3">
        <p className="flex items-center gap-1.5"><MapPin size={14} className="text-gray-400 flex-shrink-0" /> {order.deliveryAddress}{order.hostel && <span className="flex items-center gap-1 text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full"><Building2 size={11} /> {order.hostel}</span>}</p>
        <p className={`flex items-center gap-1.5 ${cutoffPast ? 'text-red-500 font-medium' : ''}`}>
          <Clock size={14} className={cutoffPast ? 'text-red-400' : 'text-gray-400'} />
          Cutoff {fmtDateTime(order.cutoffTime)}{cutoffPast && <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded-full">past cutoff</span>}
        </p>
        <button onClick={() => setShowMembers(s => !s)} className="flex items-center gap-1.5 text-gray-600 hover:text-indigo-600 transition-colors">
          <Users size={14} className="text-gray-400" /> {order.memberCount}/{order.maxPeople} joined {isFull && <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">full</span>}
        </button>
        {order.notes && <p className="flex items-start gap-1.5 text-gray-500"><StickyNote size={14} className="text-gray-400 mt-0.5 flex-shrink-0" /> {order.notes}</p>}
      </div>

      {/* Members + items */}
      {showMembers && order.members?.length > 0 && (
        <div className="mb-3 space-y-1.5 bg-gray-50 rounded-xl p-3">
          {order.members.map(m => (
            <div key={m.id} className="flex items-start gap-2 text-xs">
              <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold flex-shrink-0">{m.user?.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1">
                <span className="font-semibold text-gray-700">{m.user?.name}</span>
                {m.items && <span className="text-gray-500"> — {m.items}</span>}
                {m.amount > 0 && <span className="text-gray-400"> ({inr(m.amount)})</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My items */}
      {order.isMember && myMembership && !editing && (
        <div className="mb-3 flex items-start justify-between bg-indigo-50 rounded-xl px-3 py-2">
          <p className="text-xs text-indigo-700">
            <span className="font-semibold">Your items:</span> {myMembership.items || 'not set'}
            {myMembership.amount > 0 && <span className="text-indigo-400"> · {inr(myMembership.amount)}</span>}
          </p>
          <button onClick={startEdit} className="text-indigo-400 hover:text-indigo-600 flex-shrink-0 ml-2"><Pencil size={13} /></button>
        </div>
      )}

      {/* Join / edit inline form */}
      {(joining || editing) && (
        <div className="mb-3 space-y-2 bg-gray-50 rounded-xl p-3">
          <input className="input w-full text-sm" placeholder="Your items (e.g. 1x Paneer Roll, 1x Coke)" value={items} onChange={e => setItems(e.target.value)} />
          <input className="input w-full text-sm" type="number" min="0" step="0.01" placeholder="Your share (₹, optional)" value={amount} onChange={e => setAmount(e.target.value)} />
          <div className="flex gap-2">
            <button onClick={() => { setJoining(false); setEditing(false); }} className="btn-secondary flex-1 text-xs !py-1.5">Cancel</button>
            <button onClick={joining ? submitJoin : submitEdit} disabled={busy} className="btn-primary flex-1 text-xs !py-1.5">
              {busy ? 'Saving…' : joining ? 'Join order' : 'Save items'}
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-50">
        {canJoin && !joining && (
          <button onClick={() => { setEditing(false); setJoining(true); }} className="btn-primary text-xs flex items-center gap-1"><Plus size={13} /> Join with items</button>
        )}
        {order.isMember && !isHost && (
          <button onClick={() => onLeave(order.id)} disabled={busy} className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 px-3 py-1.5 rounded-full border border-red-100 hover:border-red-300 transition-colors">
            <LogOut size={13} /> Leave
          </button>
        )}
        {isHost && (HOST_ACTIONS[order.status] || []).map(a => (
          <button key={a.to} onClick={() => onStatus(order.id, a.to)} disabled={busy}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${a.to === 'CANCELLED' ? 'text-red-500 border-red-100 hover:border-red-300' : 'text-indigo-600 border-indigo-100 hover:border-indigo-300'}`}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FoodSharePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('open');            // 'open' | 'mine'
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ restaurant: '', deliveryAddress: '', cutoffTime: '', restaurantUrl: '', maxPeople: '', notes: '', hostel: '' });

  const openVars = { status: statusFilter || undefined };
  const openQ = useQuery(FOOD_ORDERS, { variables: openVars, skip: tab !== 'open' });
  const mineQ = useQuery(MY_FOOD_ORDERS, { skip: tab !== 'mine' });

  const refetchAll = [{ query: FOOD_ORDERS, variables: openVars }, { query: MY_FOOD_ORDERS }];

  const [create, { loading: creating }] = useMutation(CREATE_FOOD_ORDER, {
    refetchQueries: refetchAll,
    onCompleted: () => { setShowCreate(false); setForm({ restaurant: '', deliveryAddress: '', cutoffTime: '', restaurantUrl: '', maxPeople: '', notes: '', hostel: '' }); },
  });
  const [join, { loading: joiningM }] = useMutation(JOIN_FOOD_ORDER, { refetchQueries: refetchAll });
  const [leave, { loading: leaving }] = useMutation(LEAVE_FOOD_ORDER, { refetchQueries: refetchAll });
  const [setStatus, { loading: statusing }] = useMutation(UPDATE_FOOD_ORDER_STATUS, { refetchQueries: refetchAll });
  const [updateItems, { loading: updating }] = useMutation(UPDATE_MY_FOOD_ITEMS, { refetchQueries: refetchAll });
  const busy = joiningM || leaving || statusing || updating;

  const active = tab === 'open' ? openQ : mineQ;
  const orders = tab === 'open' ? (openQ.data?.foodOrders || []) : (mineQ.data?.myFoodOrders || []);

  const handleCreate = async (e) => {
    e.preventDefault();
    await create({ variables: {
      restaurant: form.restaurant,
      deliveryAddress: form.deliveryAddress,
      cutoffTime: new Date(form.cutoffTime).toISOString(),
      restaurantUrl: form.restaurantUrl || null,
      maxPeople: form.maxPeople ? parseInt(form.maxPeople, 10) : null,
      notes: form.notes || null,
      hostel: form.hostel || null,
    } });
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🍜 Food Share</h1>
          <p className="text-gray-500 text-sm">Pool a delivery order with hostel-mates · Split fees, eat together</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={14} /> Start an order</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3">
        {[{ k: 'open', label: 'Open orders' }, { k: 'mine', label: 'Mine' }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${tab === t.k ? 'bg-gray-800 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Status filter (open tab) */}
      {tab === 'open' && (
        <div className="flex gap-1 flex-wrap mb-4">
          <button onClick={() => setStatusFilter('')}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${!statusFilter ? 'bg-indigo-600 text-white border-transparent' : 'border-gray-200 text-gray-400 hover:border-indigo-300'}`}>All</button>
          {STATUSES.map(s => (
            <button key={s.value} onClick={() => setStatusFilter(f => f === s.value ? '' : s.value)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${statusFilter === s.value ? 'bg-indigo-600 text-white border-transparent' : 'border-gray-200 text-gray-400 hover:border-indigo-300'}`}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* States */}
      {active.loading && (
        <div className="flex items-center justify-center gap-2 py-16 text-gray-400"><Loader2 size={18} className="animate-spin" /> Loading orders…</div>
      )}
      {active.error && !active.loading && (
        <div className="card !p-5 flex items-center gap-3 text-sm text-red-600 bg-red-50">
          <AlertCircle size={18} className="flex-shrink-0" />
          <span>Couldn't load food orders — {active.error.message}</span>
          <button onClick={() => active.refetch()} className="ml-auto text-xs font-semibold underline">Retry</button>
        </div>
      )}
      {!active.loading && !active.error && orders.length === 0 && (
        <div className="text-center py-16">
          <div className="text-5xl mb-3">🍜</div>
          <p className="text-gray-500">{tab === 'mine' ? "You haven't joined any orders yet." : 'No open orders right now — start one!'}</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">+ Start an order</button>
        </div>
      )}

      {/* Grid */}
      {!active.loading && !active.error && orders.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {orders.map(o => (
            <OrderCard key={o.id} order={o} currentUserId={user?.id} busy={busy}
              onJoin={(orderId, items, amount) => join({ variables: { orderId, items, amount } })}
              onLeave={orderId => leave({ variables: { orderId } })}
              onStatus={(orderId, status) => setStatus({ variables: { orderId, status } })}
              onUpdateItems={(orderId, items, amount) => updateItems({ variables: { orderId, items, amount } })} />
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">Start a group food order</h2>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <input required className="input w-full" placeholder="Restaurant (e.g. Domino's, Biryani Blues)" value={form.restaurant} onChange={e => setForm(f => ({ ...f, restaurant: e.target.value }))} />
              <input className="input w-full" type="url" placeholder="Restaurant / menu link (optional)" value={form.restaurantUrl} onChange={e => setForm(f => ({ ...f, restaurantUrl: e.target.value }))} />
              <input required className="input w-full" placeholder="Delivery address (gate / block)" value={form.deliveryAddress} onChange={e => setForm(f => ({ ...f, deliveryAddress: e.target.value }))} />
              <input className="input w-full" placeholder="Hostel (optional)" value={form.hostel} onChange={e => setForm(f => ({ ...f, hostel: e.target.value }))} />
              <div>
                <label className="text-xs text-gray-400 block mb-1">Cutoff time — joining closes after this</label>
                <input required className="input w-full" type="datetime-local" value={form.cutoffTime} onChange={e => setForm(f => ({ ...f, cutoffTime: e.target.value }))} />
              </div>
              <input className="input w-full" type="number" min="2" placeholder="Max people (default 10)" value={form.maxPeople} onChange={e => setForm(f => ({ ...f, maxPeople: e.target.value }))} />
              <textarea className="input w-full resize-none h-20 text-sm" placeholder="Notes (payment mode, coupon plan…)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creating} className="btn-primary flex-1">{creating ? 'Starting…' : 'Start order'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
